import { Launch } from "@mui/icons-material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import { Alert, Avatar, Box, CardContent, CircularProgress, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useAppState } from "../../hooks/useAppState";
import { useCourseProgress } from "../../hooks/useCourseProgress";
import { useProgressModal } from "../../hooks/useProgressModal";
import { MyCoursesQuery_myCourses_rosterUsers } from "../../pages/Library/__generated__/MyCoursesQuery";
import SetupInstructions from "../../pages/Library/SetupInstructions";
import { useCourseDetails } from "../../pages/Library/useCourseDetails";
import ProgressOverview from "../Progress/ProgressOverview";
import CourseAssets from "./CourseAssets";
import {
  CertLink,
  CertText,
  CourseName,
  DownloadCertLink,
  ExpirationText,
  HeaderContentWrapper,
  LibraryButton,
  LibraryCardElement,
} from "./LibraryCard.elements";

interface LibraryCardProps {
  course: MyCoursesQuery_myCourses_rosterUsers;
}

export const LibraryCard: React.FC<LibraryCardProps> = ({ course }) => {
  const { setInstance, setCourse } = useAppState();
  const { progress, expiration, completion, openCert, updateCourseProgressState } = useCourseProgress(
    course.course.trainingId,
    course.course.id,
    course.trainingInstance?.id,
    course.id,
  );

  const { getCourseDetails, gotDetails } = useCourseDetails(course.course.trainingId, course.trainingInstance?.id);
  const [opening, setOpening] = useState(false);
  const [isLoadingCert, setIsLoadingCert] = useState(false);

  useEffect(() => {
    updateCourseProgressState();
  }, [updateCourseProgressState]);

  const navigate = useNavigate();

  const { openModal } = useProgressModal();

  const openProgress = useCallback(async () => {
    if (!gotDetails) {
      await getCourseDetails();
    }

    setCourse(course.course.trainingId);

    openModal({
      courseId: course.course.trainingId,
      courseUuid: course.course.id,
      instanceId: course.trainingInstance?.id,
      fromLibrary: true,
    });
  }, [
    course.course.id,
    course.course.trainingId,
    course.trainingInstance?.id,
    openModal,
    getCourseDetails,
    gotDetails,
    setCourse,
  ]);

  const openCourse = useCallback(() => {
    if (course.trainingInstance?.id) setInstance(course.trainingInstance?.id);
    setCourse(course.course.trainingId);
    navigate(`/${course.course.trainingId}`);
  }, [course.course.trainingId, course.trainingInstance?.id, navigate, setCourse, setInstance]);

  const handleOpenCert = useCallback(async () => {
    if (isLoadingCert) return;
    setIsLoadingCert(true);
    try {
      await openCert();
    } catch {
      // Error is handled by the openCert function, we just need to clear loading state
    } finally {
      setIsLoadingCert(false);
    }
  }, [isLoadingCert, openCert]);

  useEffect(() => {
    if (opening && gotDetails) {
      openCourse();
      setOpening(false);
    }
  }, [opening, gotDetails, openCourse]);

  const isExpired = expiration?.isExpired;
  const getButtonLabel = () => {
    if (isExpired) return "Expired";
    return course.trainingInstance ? "Open Course" : "Start Course";
  };
  const buttonLabel = getButtonLabel();
  const certLinkText = isLoadingCert ? "Loading certificate..." : "Download your certificate of completion";

  return (
    <LibraryCardElement onMouseEnter={() => !isExpired && getCourseDetails()}>
      <CardContent sx={{ width: "100%", padding: "0" }}>
        <Stack gap={2}>
          <HeaderContentWrapper className="headerContentWrapper">
            <Box sx={{ flexGrow: 1 }}>
              <CourseName
                fontWeight="600"
                component={isExpired ? "h3" : "h2"}
                className={isExpired ? "expired" : undefined}
                onClick={isExpired ? undefined : openCourse}
                sx={{ cursor: isExpired ? undefined : "pointer" }}
              >
                {course.course.name}
              </CourseName>
              {expiration && (
                <ExpirationText className={isExpired ? "expired" : ""}>
                  <TimelapseIcon sx={{ width: "0.7em", height: "0.7em", verticalAlign: "sub" }} />{" "}
                  {expiration.expirationText} ({expiration.expirationDate})
                </ExpirationText>
              )}
            </Box>
            <LibraryButton
              variant="contained"
              disabled={isExpired || opening}
              disableElevation
              startIcon={isExpired ? undefined : <PlayArrowIcon sx={{ marginTop: "0.1em" }} />}
              onClick={isExpired ? undefined : () => setOpening(true)}
            >
              {buttonLabel}
            </LibraryButton>
          </HeaderContentWrapper>
          {completion && (
            <Alert severity="success" icon={false} sx={{ textAlign: "center", justifyContent: "center" }}>
              <Typography sx={{ marginBottom: "0.5em" }} fontWeight="700">
                Congratulations on completing your course!
              </Typography>
              <DownloadCertLink
                onClick={handleOpenCert}
                aria-busy={isLoadingCert}
                aria-disabled={isLoadingCert}
                sx={{
                  pointerEvents: isLoadingCert ? "none" : "auto",
                  opacity: isLoadingCert ? 0.7 : 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5em",
                }}
              >
                {isLoadingCert && <CircularProgress size={16} aria-hidden="true" sx={{ verticalAlign: "middle" }} />}
                {certLinkText}
                {isLoadingCert && (
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      width: "1px",
                      height: "1px",
                      padding: 0,
                      margin: "-1px",
                      overflow: "hidden",
                      clip: "rect(0, 0, 0, 0)",
                      whiteSpace: "nowrap",
                      border: 0,
                    }}
                    role="status"
                    aria-live="polite"
                  >
                    Loading your certificate of completion, please wait
                  </Box>
                )}
              </DownloadCertLink>
            </Alert>
          )}
          {progress && course.trainingInstance && expiration && (
            <Box>
              <ProgressOverview
                compact
                title="Progress"
                progress={progress}
                expiration={expiration}
                openProgress={openProgress}
              />

              {course.giacPurchased && course.certification && (
                <Box sx={{ display: "flex", marginTop: "1em" }}>
                  <Avatar src={course.certification.imageUrl ?? undefined} sx={{ height: "1.8em", width: "1.8em" }}>
                    G
                  </Avatar>{" "}
                  <CertText>
                    {course.certification.name} ({course.certification.initials})
                  </CertText>
                  <CertLink href="https://exams.giac.org/">
                    Manage Exam Attempt <Launch sx={{ height: "0.8em", width: "0.8em", verticalAlign: "sub" }} />
                  </CertLink>
                </Box>
              )}
            </Box>
          )}

          <CourseAssets
            hasMyLabs={course.course.hasMyLabs}
            handouts={course.course.handouts}
            licenses={course.course.licenses}
          />

          <SetupInstructions
            hasMyLabs={course.course.hasMyLabs}
            shortName={course.course.shortName ?? undefined}
            shippingTracking={course.shippingTracking}
            quickstartGuideUrl={course.course.quickstartGuideUrl ?? undefined}
          />
        </Stack>
      </CardContent>
    </LibraryCardElement>
  );
};
